import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDateTime, statusBadgeClass, statusLabel, formatApiError, naira } from "../lib/format";
import { Card, Loading, EmptyState } from "../components/UI";
import { Inbox, Mail, Phone, X, UserPlus, FileSignature } from "lucide-react";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [converting, setConverting] = useState(null);
  const [linking, setLinking] = useState(false);
  const nav = useNavigate();

  const reload = async () => {
    const [l, u, t] = await Promise.all([
      api.get("/leads"),
      api.get("/units").catch(() => ({ data: [] })),
      api.get("/tenants").catch(() => ({ data: [] })),
    ]);
    setLeads(l.data);
    setUnits(u.data);
    setTenants(t.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const linkTenant = async (tenantId) => {
    setLinking(true);
    try {
      const { data } = await api.post(`/leads/${selected.id}/link-tenant`, { tenant_id: tenantId || null });
      toast.success(tenantId ? `Linked to ${data.linked_tenant_name}` : "Unlinked");
      setSelected({ ...selected, linked_tenant_id: data.linked_tenant_id, linked_tenant_name: data.linked_tenant_name, status: data.status });
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLinking(false); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/leads/${id}`, { status }); toast.success("Updated"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const convert = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      const { data } = await api.post(`/leads/${converting.id}/convert`, {
        unit_id: f.get("unit_id") || null,
        lease_start_date: f.get("start_date") || null,
        term_months: 12,
      });
      const msg = data.temp_password
        ? `Tenant created. Temp password: ${data.temp_password} (also emailed).`
        : "Lead linked to existing tenant.";
      toast.success(msg);
      setConverting(null);
      setSelected(null);
      reload();
      if (data.lease_id) {
        setTimeout(() => nav(`/leases/${data.lease_id}`), 600);
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  const vacantUnits = units.filter(u => u.status === "vacant");

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Public Enquiries</h1>
        <p className="text-[#5E7166] text-sm">{leads.length} leads from the public website.</p>
      </div>

      <Card className="!p-0 overflow-hidden">
        {leads.length === 0 ? <EmptyState icon={Inbox} title="No enquiries yet" description="Leads from the public home page will appear here."/> :
          <div className="overflow-x-auto">
            <table className="modav-table">
              <thead><tr><th>Date</th><th>Name</th><th>Contact</th><th>Interested In</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {leads.map(l => (
                  <tr key={l.id} className="cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-${l.id}`}>
                    <td className="text-xs">{formatDateTime(l.created_at)}</td>
                    <td className="font-semibold">{l.name}</td>
                    <td className="text-xs">
                      <div className="flex items-center gap-1"><Mail size={11}/>{l.email}</div>
                      {l.phone && <div className="flex items-center gap-1"><Phone size={11}/>{l.phone}</div>}
                    </td>
                    <td>{l.unit?.unit_name || <span className="text-[#5E7166]">General</span>}</td>
                    <td><span className={statusBadgeClass(l.status)}>{statusLabel(l.status)}</span></td>
                    <td>
                      <select value={l.status} onChange={(e) => { e.stopPropagation(); updateStatus(l.id, e.target.value); }} onClick={(e) => e.stopPropagation()} className="text-xs border border-[#E5EBE8] rounded p-1">
                        <option value="new">New</option><option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option><option value="closed_won">Won</option><option value="closed_lost">Lost</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </Card>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end">
          <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-6" data-testid="lead-drawer">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl font-bold">{selected.name}</h3>
              <button onClick={() => setSelected(null)}><X size={20}/></button>
            </div>
            <div className="space-y-3 text-sm">
              <div><span className="text-[#5E7166] text-xs uppercase tracking-wider">Email</span><div>{selected.email}</div></div>
              <div><span className="text-[#5E7166] text-xs uppercase tracking-wider">Phone</span><div>{selected.phone || "—"}</div></div>
              <div><span className="text-[#5E7166] text-xs uppercase tracking-wider">Unit</span><div>{selected.unit?.unit_name || "General enquiry"}</div></div>
              <div><span className="text-[#5E7166] text-xs uppercase tracking-wider">Message</span><div className="whitespace-pre-wrap mt-1 bg-[#F7F9F8] p-3 rounded-lg">{selected.message}</div></div>

              {selected.linked_tenant_name && (
                <div className="bg-[#E8F5EE] border border-[#0E5C3A]/30 rounded-lg p-3 text-sm">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#0E5C3A]">Linked Tenant</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="font-semibold">{selected.linked_tenant_name}</span>
                    <button onClick={() => linkTenant(null)} disabled={linking} className="text-xs text-[#C92A2A] hover:underline" data-testid="unlink-tenant-btn">Unlink</button>
                  </div>
                </div>
              )}

              {!selected.linked_tenant_name && selected.status !== "closed_won" && (
                <div className="bg-[#F8FBF9] border border-[#E5EBE8] rounded-lg p-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#5E7166] mb-1.5">Link to Existing Tenant</div>
                  <p className="text-[10px] text-[#5E7166] mb-2">If this enquiry is from a current tenant about a new unit, link it here instead of converting.</p>
                  <select
                    defaultValue=""
                    onChange={(e) => e.target.value && linkTenant(e.target.value)}
                    className="input-modav text-sm"
                    disabled={linking}
                    data-testid="link-tenant-select"
                  >
                    <option value="">— Select existing tenant —</option>
                    {tenants.map(t => (
                      <option key={t.id} value={t.id}>{t.full_name} ({t.email})</option>
                    ))}
                  </select>
                </div>
              )}

              {selected.status !== "closed_won" && !selected.linked_tenant_name && (
                <button onClick={() => setConverting(selected)} className="btn-gold w-full justify-center" data-testid="convert-lead-btn">
                  <UserPlus size={14}/> Convert to Tenant + Draft Lease
                </button>
              )}
              {selected.status === "closed_won" && selected.converted_lease_id && (
                <button onClick={() => nav(`/leases/${selected.converted_lease_id}`)} className="btn-emerald w-full justify-center">
                  <FileSignature size={14}/> Open Draft Lease
                </button>
              )}
              <div className="flex gap-2 pt-2">
                <a href={`mailto:${selected.email}?subject=Re: Your Modav Homes enquiry`} className="btn-outline-emerald flex-1 justify-center">Reply by Email</a>
                {selected.phone && <a href={`https://wa.me/${selected.phone.replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer" className="btn-outline-emerald flex-1 justify-center">WhatsApp</a>}
              </div>
            </div>
          </div>
        </div>
      )}

      {converting && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-md" data-testid="convert-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Convert {converting.name}</h3>
              <button onClick={() => setConverting(null)}><X size={20}/></button>
            </div>
            <form onSubmit={convert} className="space-y-3">
              <div className="bg-[#FDF6E3]/40 p-3 rounded-lg text-xs text-[#A0801C]">
                Creates the tenant account (sends welcome email with temp password) and — if a unit is selected — generates a draft lease using current pricing.
              </div>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Assign Unit (optional)</span>
                <select name="unit_id" defaultValue={converting.interested_unit_id || ""} className="input-modav mt-1" data-testid="convert-unit-select">
                  <option value="">— No unit yet (just create tenant) —</option>
                  {vacantUnits.map(u => <option key={u.id} value={u.id}>{u.unit_name} — {u.unit_type_name} ({naira(u.current_rent_amount)})</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Lease Start Date (if assigning unit)</span>
                <input type="date" name="start_date" defaultValue={new Date().toISOString().split("T")[0]} className="input-modav mt-1"/>
              </label>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setConverting(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
                <button type="submit" className="btn-gold flex-1 justify-center" data-testid="confirm-convert-btn">Convert Now</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
